import styles from "./Button.module.scss";
import { classNames } from "../../Utilities";
import { Severity } from "../../Helpers";
import { IconDefinition, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FC, PropsWithChildren } from "react";

export interface ButtonProps {
    icon?: IconDefinition;
    iconSide?: "left" | "right";
    isBusy?: () => boolean;
    severity?: Severity;
    busyText?: React.ReactNode;
    size?: ButtonSize;
    empty?: boolean;
    squareLeft?: boolean;
    squareRight?: boolean;
    shadow?: boolean;
}

export enum ButtonSize {
    XSmall,
    Small,
    Standard,
    Large
}

export const Button: FC<PropsWithChildren<React.ButtonHTMLAttributes<HTMLButtonElement> & ButtonProps>> = (props) => {
    const { icon, isBusy, severity, busyText, className, iconSide, type, empty, squareLeft, squareRight, disabled, ...rest } = props;

    const getSeverityClass = (): string => {
        switch (props.severity) {
            case Severity.Secondary:
                return styles.secondary;
            case Severity.Tertiary:
                return styles.tertiary;
            case Severity.Info:
                return styles.info;
            case Severity.Success:
                return styles.success;
            case Severity.Warning:
                return styles.warning;
            case Severity.Danger:
                return styles.danger;
            default:
                return styles.primary;
        }
    };

    const getSizeClass = (): string => {
        switch (props.size) {
            case ButtonSize.XSmall:
                return styles.xsmall;
            case ButtonSize.Small:
                return styles.small;
            case ButtonSize.Standard:
                return styles.standard;
            case ButtonSize.Large:
                return styles.large;
            default:
                return styles.standard;
        }
    };

    return (
        <button
            type={type ?? "button"}
            className={classNames([
                styles.button,
                props.squareLeft && styles.squareLeft,
                props.squareRight && styles.squareRight,
                getSeverityClass(),
                getSizeClass(),
                props.className,
                !!props.shadow && styles.shadow,
                !!props.empty && styles.empty
            ])}
            disabled={disabled || (isBusy?.() ?? false)}
            {...rest}
        >

            {(!props.isBusy?.() || !props.busyText) && (
                <span className={classNames([
                    styles.flex,
                    (props.isBusy?.() && !props.busyText) ? styles.reducedOpacity : undefined
                ])}
                >
                    {!!props.icon && props.iconSide !== "right" && (
                        <FontAwesomeIcon icon={props.icon} />
                    )}
                    {!!props.children && (
                        <span>
                            {props.children}
                        </span>
                    )}
                    {!!props.icon && props.iconSide === "right" && (
                        <FontAwesomeIcon icon={props.icon} />
                    )}
                </span>
            )}

            {!!props.isBusy?.() && (
                <span
                    className={classNames([
                        styles.flex,
                        !props.busyText ? styles.floatingLoader : undefined
                    ])}
                >
                    <FontAwesomeIcon icon={faSpinner} spinPulse />
                    {!!props.busyText && (
                        <span>{props.busyText}</span>
                    )}
                </span>
            )}

        </button>
    );
};